import { Commentary, CommentaryV2, isDefined } from '@aloft/toolkit-shared';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import { CommentaryState, SyncResult } from '../types.ts';
import { clearOrder } from './actions/clearOrder.ts';
import { submitReconciliation, submitReconciliationDataSheet } from './reconciliationSlice.ts';
import { getBumpedSyncState } from './utils/getBumpedSyncState.ts';

export const initialCommentaryState: CommentaryState = {
  marketConditionsV2: {
    filter: {
      type: 'Zip Code',
    },
    estimatedDemandSupply: 'Shortage',
    estimatedExposureTime: 'Less than 30 days',
  },
};

const commentarySlice = createSlice({
  name: 'commentary',
  initialState: initialCommentaryState,
  reducers: {
    setCommentary: (state, action: PayloadAction<Commentary>) => {
      Object.assign(state, {
        ...action.payload,
        syncState: getBumpedSyncState(state.syncState),
      });
    },
    setCommentaryV2: (state, action: PayloadAction<CommentaryV2>) => {
      Object.assign(state, {
        ...action.payload,
        syncState: getBumpedSyncState(state.syncState),
      });
    },
    commentaryMutationComplete(state, action: PayloadAction<SyncResult>) {
      const { payload } = action;
      const { status } = payload;

      if (state.syncState) {
        Object.assign(state.syncState, { status });

        if (status === 'success') {
          Object.assign(state.syncState, { serverRevision: payload.serverRevision });
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(clearOrder, () => initialCommentaryState);
    builder.addCase(submitReconciliationDataSheet, (state, action) => {
      const { reconciliationCommentary } = action.payload;
      if (isDefined(reconciliationCommentary)) {
        Object.assign(state, {
          syncState: getBumpedSyncState(state.syncState),
          reconciliation: {
            ...state.reconciliation,
            reconciliationCommentary,
          },
        });
      }
    });
    builder.addCase(submitReconciliation, (state, action) => {
      const { reconciliationCommentary, highestAndBestUseCommentary, revisionCommentary, alternativeComparablesCommentary } = action.payload;

      if (isDefined(reconciliationCommentary) && isDefined(highestAndBestUseCommentary)) {
        Object.assign(state, {
          syncState: getBumpedSyncState(state.syncState),
          reconciliation: {
            ...state.reconciliation,
            reconciliationCommentary,
            highestAndBestUseCommentary,
            revisionCommentary,
            alternativeComparablesCommentary,
          },
        });
      }
    });
  },
});

export const { setCommentary, setCommentaryV2, commentaryMutationComplete } =
  commentarySlice.actions;

export const commentaryReducer = commentarySlice.reducer;
